import React from "react";
import { useTranslation } from "react-i18next";

interface ProposalFieldProps {
  field: string;
  label: string;
  value: string;
  proposal?: string;
  isProposal: boolean;
  onChange: (value: string) => void;
  onAccept: () => void;
  onReject: () => void;
  isTextarea?: boolean;
  rows?: number;
}

/**
 * Form field with AI proposal support.
 * Shows proposal badge and accept/reject actions when a proposal exists.
 */
export const ProposalField: React.FC<ProposalFieldProps> = ({
  field,
  label,
  value,
  proposal,
  isProposal,
  onChange,
  onAccept,
  onReject,
  isTextarea = true,
  rows = 4,
}) => {
  const { t } = useTranslation(["recruiting"]);
  const hasProposal = isProposal && !!proposal;
  const isProposed = hasProposal && value === proposal;
  const showProposal = hasProposal && value !== proposal;

  return (
    <div className={`tech-check-field ${isProposed ? "has-proposal" : ""}`}>
      <label>
        {label}
        {hasProposal && (
          <span className="proposal-badge">
            {t("techCheck.proposal.aiBadge")}
          </span>
        )}
      </label>

      {isTextarea ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={isProposed ? "proposal-field" : ""}
          rows={rows}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={isProposed ? "proposal-field" : ""}
        />
      )}

      {showProposal && (
        <div className="proposal-actions">
          <div className="proposal-preview">
            <strong>{t("techCheck.proposal.aiProposalLabel")}</strong>
            <p>{proposal}</p>
          </div>
          <div className="proposal-buttons">
            <button
              type="button"
              onClick={onAccept}
              className="btn btn-sm btn-primary"
            >
              {t("techCheck.proposal.accept")}
            </button>
            <button
              type="button"
              onClick={onReject}
              className="btn btn-sm btn-outline"
            >
              {t("techCheck.proposal.reject")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProposalField;
